import { StylesProvider } from "@material-ui/core/styles";
import * as Sentry from "@sentry/react";
import { BrowserTracing } from "@sentry/tracing";
import "bootstrap/dist/css/bootstrap.css";
import React from "react";
import { createRoot } from "react-dom/client";
import { Provider } from "react-redux";
import { BrowserRouter as Router } from "react-router-dom";
import App from "./App";
import ErrorBoundary from "./components/atoms/FlowioErrorBoundary";
import { BugReportModal } from "./components/molecules/modals/FeedbackModal";
import {
  FlowioastContainer,
  FlowioIntlProvider,
  FlowioThemeProvider,
  GlobalConfirmationModalProvider,
  WebViewEventListenerAssigner,
} from "./components/providers";
import ActionsQueueContextProvider from "./components/providers/ActionsQueueContextProvider";
import BreadcrumbsContextProvider from "./components/providers/BreadcrumbsContextProvider";
import LocationHistoryProvider from "./components/providers/LocationHistoryProvider";
import ReactTourProvider from "./components/providers/ReactTourProvider";
import ModalContextProvider from "./components/providers/ModalContextProvider/ModalContextProvider";
import "./config";
import { SENTRY_DSN, SENTRY_TRACE_SAMPLE_RATE } from "./config/constants";
import { unregister } from "./registerServiceWorker";
import { store } from "./store/configureStore";
import { getBasenameFromHtml } from "./utils/global";

Sentry.init({
  dsn: SENTRY_DSN,
  integrations: [new BrowserTracing()],
  tracesSampleRate: SENTRY_TRACE_SAMPLE_RATE,
});

// eslint-disable-next-line import/prefer-default-export
const baseUrl = getBasenameFromHtml();

const rootElement = document.getElementById("root");
const root = createRoot(rootElement as HTMLElement);

root.render(
  <Provider store={store}>
    <FlowioastContainer />

    <Router basename={baseUrl}>
      <LocationHistoryProvider>
        <StylesProvider injectFirst>
          <FlowioThemeProvider>
            <FlowioIntlProvider>
              <GlobalConfirmationModalProvider>
                <BugReportModal />
                <ErrorBoundary>
                  <WebViewEventListenerAssigner>
                    <ActionsQueueContextProvider>
                      <ReactTourProvider>
                        <BreadcrumbsContextProvider>
                          <ModalContextProvider>
                            <App />
                          </ModalContextProvider>
                        </BreadcrumbsContextProvider>
                      </ReactTourProvider>
                    </ActionsQueueContextProvider>
                  </WebViewEventListenerAssigner>
                </ErrorBoundary>
              </GlobalConfirmationModalProvider>
            </FlowioIntlProvider>
          </FlowioThemeProvider>
        </StylesProvider>
      </LocationHistoryProvider>
    </Router>
  </Provider>
);

unregister();
